import { Injectable, signal } from '@angular/core';

/**
 * FolioService — utilidades de folio para toda la app (Fase C0).
 *
 * `copyFolio` copia un folio al portapapeles usando la Clipboard API
 * moderna con fallback a textarea + execCommand('copy') para navegadores
 * o contextos sin `navigator.clipboard`. Expone `lastCopied` como feedback
 * visual mínimo: contiene el folio copiado por ~1.5s y luego se limpia.
 * Sin dependencias externas.
 */
@Injectable({ providedIn: 'root' })
export class FolioService {
  /** Último folio copiado con éxito (se limpia tras un breve timeout). */
  readonly lastCopied = signal<string | null>(null);

  private resetTimer: ReturnType<typeof setTimeout> | null = null;

  copyFolio(folio: string, event?: Event): void {
    event?.stopPropagation();

    if (!folio) {
      return;
    }

    const onSuccess = () => this.flagCopied(folio);

    if (
      typeof navigator !== 'undefined' &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === 'function'
    ) {
      navigator.clipboard.writeText(folio).then(
        () => onSuccess(),
        () => this.fallbackCopy(folio, onSuccess),
      );
      return;
    }

    this.fallbackCopy(folio, onSuccess);
  }

  private fallbackCopy(folio: string, onSuccess: () => void): void {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = folio;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.top = '-9999px';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (ok) {
        onSuccess();
      }
    } catch {
      /* no-op: copiar al portapapeles no está disponible */
    }
  }

  private flagCopied(folio: string): void {
    this.lastCopied.set(folio);
    if (this.resetTimer) {
      clearTimeout(this.resetTimer);
    }
    this.resetTimer = setTimeout(() => {
      this.lastCopied.set(null);
      this.resetTimer = null;
    }, 1500);
  }
}
